import { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate } from "react-router-dom";
import { routePaths } from "../constants/routes";
import jiraAgileHierarchyImage from "../assets/jira-agile-hierarchy.svg";
import jiraBugAnatomyImage from "../assets/jira-bug-anatomy.svg";
import jiraSeverityPriorityImage from "../assets/jira-severity-priority-matrix.svg";
import {
  jiraAgileBasics,
  jiraAdvancedJqlQueries,
  jiraBestInterviewAnswers,
  jiraBonusSections,
  jiraBugReportExample,
  jiraBugCreationTemplate,
  jiraBugLifeCycleStatuses,
  jiraDashboardReports,
  jiraDetailedQaAnswers,
  jiraFullTicketExample,
  jiraGlossaryTerms,
  jiraInterviewQuestionBank,
  jiraInterviewAnswers,
  jiraIssueTypesMatrix,
  jiraModules,
  jiraMncInterviewQuestions,
  jiraNotesPackId,
  jiraPracticeAssignments,
  jiraRealTimeExperience,
  jiraRoadmapPhases,
  jiraJqlUseCases,
  jiraRealTimeBugExamples,
  jiraReadyMadeBugExamples,
  jiraSeverityPriorityExamples,
} from "../content/jiraNotesContent";
import { getPremiumAccess, hasPremiumAccess } from "../services/premiumNotesService";
import "../style/jira-notes-download.css";

export default function JiraNotesDownload() {
  const [access, setAccess] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
    if (hasPremiumAccess(jiraNotesPackId)) {
      setAccess(getPremiumAccess(jiraNotesPackId));
    }
  }, []);

  const printableDate = useMemo(() => new Date().toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }), []);

  if (ready && !access) {
    return <Navigate to={routePaths.jiraNotesCheckout} replace />;
  }

  const handlePrint = () => window.print();

  const renderPrintTable = (title, headers, rows) => (
    <section className="jira-print-section">
      {title ? <h2>{title}</h2> : null}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  style={{ textAlign: "left", padding: "10px", borderBottom: "1px solid #cbd5e1" }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row[0]}-${index}`}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={`${cell}-${cellIndex}`}
                    style={{ padding: "10px", borderBottom: "1px solid #e2e8f0", verticalAlign: "top" }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  return (
    <main className="jira-download-page">
      <div className="jira-download-toolbar no-print">
        <div>
          <span>Premium unlocked</span>
          <strong>{access?.purchaseId || "Preparing access"}</strong>
        </div>
        <div className="jira-download-actions">
          <button type="button" onClick={handlePrint}>Print / Save as PDF</button>
          <NavLink to={routePaths.jiraNotes}>Back to premium page</NavLink>
        </div>
      </div>

      <article className="jira-print-document">
        <header className="jira-print-hero">
          <div>
            <span>QA Coder Premium Pack</span>
            <h1>JIRA for QA: premium study notes and interview guide</h1>
            <p>
              Printable premium notes covering workflows, issue types, JQL, dashboards, bug lifecycle,
              real-time QA usage, and enterprise interview preparation.
            </p>
          </div>
          <div className="jira-print-meta">
            <div>
              <small>Pack</small>
              <strong>JIRA QA Notes</strong>
            </div>
            <div>
              <small>Price paid</small>
              <strong>Rs. 99</strong>
            </div>
            <div>
              <small>Generated</small>
              <strong>{printableDate}</strong>
            </div>
          </div>
        </header>

        <section className="jira-print-section">
          <h2>Learning roadmap</h2>
          <div className="jira-print-roadmap">
            {jiraRoadmapPhases.map((phase) => (
              <div key={phase.title} className="jira-print-roadmap-card">
                <strong>{phase.title}</strong>
                <small>{phase.duration}</small>
                <ul>
                  {phase.topics.map((topic) => (
                    <li key={topic}>{topic}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="jira-print-section">
          <h2>Visual diagrams for better understanding</h2>
          <div className="jira-print-visual-grid">
            <article className="jira-print-module">
              <h3>Agile hierarchy</h3>
              <img src={jiraAgileHierarchyImage} alt="JIRA Agile hierarchy" className="jira-print-visual-image" />
              <p>Shows how epic, story, task, sub-task, bug, and QA verification connect in a real project.</p>
            </article>
            <article className="jira-print-module">
              <h3>Severity vs priority</h3>
              <img src={jiraSeverityPriorityImage} alt="Severity and priority matrix" className="jira-print-visual-image" />
              <p>Useful for interviews and real bug triage when deciding technical impact versus business urgency.</p>
            </article>
            <article className="jira-print-module">
              <h3>Bug report anatomy</h3>
              <img src={jiraBugAnatomyImage} alt="JIRA bug report anatomy" className="jira-print-visual-image" />
              <p>Helps students remember the exact structure of a strong professional JIRA ticket.</p>
            </article>
          </div>
        </section>

        <section className="jira-print-section">
          <h2>Detailed modules</h2>
          {jiraModules.map((module) => (
            <article key={module.title} className="jira-print-module">
              <h3>{module.title}</h3>
              <p>{module.answer}</p>
              <ul>
                {module.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        <section className="jira-print-section">
          <h2>Detailed QA questions and answers</h2>
          {jiraDetailedQaAnswers.map((item) => (
            <article key={item.question} className="jira-print-module">
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
              {item.table && renderPrintTable("", item.table.headers, item.table.rows)}
              {item.extraTable && renderPrintTable("", item.extraTable.headers, item.extraTable.rows)}
              {item.bullets && (
                <ul>
                  {item.bullets.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              )}
              {item.example && <p><strong>Example:</strong> {item.example}</p>}
            </article>
          ))}
        </section>

        {renderPrintTable("Agile basics", ["Agile Term", "Meaning"], jiraAgileBasics)}
        {renderPrintTable("Issue types", ["Issue Type", "Meaning", "QA Example"], jiraIssueTypesMatrix)}
        {renderPrintTable("Bug life cycle", ["Status", "Meaning"], jiraBugLifeCycleStatuses)}
        {renderPrintTable("Bug creation template", ["Field", "Example"], jiraBugCreationTemplate)}
        {renderPrintTable("Real-time bug examples", ["Application", "Bug Scenario", "Severity", "Priority"], jiraRealTimeBugExamples)}
        {renderPrintTable("Severity vs priority", ["Case", "Meaning", "Example"], jiraSeverityPriorityExamples)}
        {renderPrintTable("Reports and dashboards", ["Report", "Use"], jiraDashboardReports)}
        {renderPrintTable("JQL use cases", ["Requirement", "JQL Query"], jiraJqlUseCases)}

        <section className="jira-print-section">
          <h2>Real-time QA experience</h2>
          {jiraRealTimeExperience.map((item) => (
            <article key={item.title} className="jira-print-module">
              <h3>{item.title}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </section>

        <section className="jira-print-section">
          <h2>Advanced JQL queries</h2>
          <div className="jira-print-query-list">
            {jiraAdvancedJqlQueries.map((query) => (
              <code key={query}>{query}</code>
            ))}
          </div>
        </section>

        <section className="jira-print-section">
          <h2>Bug report example</h2>
          <article className="jira-print-module">
            <h3>{jiraBugReportExample.title}</h3>
            <ul>
              {jiraBugReportExample.fields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
            <ol>
              {jiraBugReportExample.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p><strong>Expected:</strong> {jiraBugReportExample.expected}</p>
            <p><strong>Actual:</strong> {jiraBugReportExample.actual}</p>
          </article>
        </section>

        <section className="jira-print-section">
          <h2>Interview answers</h2>
          {[...jiraInterviewAnswers, ...jiraMncInterviewQuestions].map((item) => (
            <article key={item.question} className="jira-print-module">
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
          {jiraBestInterviewAnswers.map((item) => (
            <article key={item.title} className="jira-print-module">
              <h3>{item.title}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
          <article className="jira-print-module">
            <h3>Interview question bank preview</h3>
            <ul>
              {jiraInterviewQuestionBank.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          </article>
        </section>

        <section className="jira-print-section">
          <h2>Ready-made bug examples</h2>
          {jiraReadyMadeBugExamples.map((item) => (
            <article key={item.title} className="jira-print-module">
              <h3>{item.title}</h3>
              <p><strong>Summary:</strong> {item.summary}</p>
              <ol>
                {item.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>
              <p><strong>Actual:</strong> {item.actual}</p>
              <p><strong>Expected:</strong> {item.expected}</p>
              <p><strong>Severity:</strong> {item.severity}</p>
              <p><strong>Priority:</strong> {item.priority}</p>
              <p><strong>Root Cause:</strong> {item.rootCause}</p>
            </article>
          ))}
        </section>

        <section className="jira-print-section">
          <h2>Practice assignments</h2>
          {jiraPracticeAssignments.map((item) => (
            <article key={item.title} className="jira-print-module">
              <h3>{item.title}</h3>
              {item.points && (
                <ul>
                  {item.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              )}
              {item.table && renderPrintTable("", item.table.headers, item.table.rows)}
            </article>
          ))}
        </section>

        <section className="jira-print-section">
          <h2>Complete real-time JIRA ticket</h2>
          <article className="jira-print-module">
            <h3>{jiraFullTicketExample.summary}</h3>
            <p><strong>Project:</strong> {jiraFullTicketExample.project}</p>
            <p><strong>Issue Type:</strong> {jiraFullTicketExample.issueType}</p>
            <p>{jiraFullTicketExample.description}</p>
            <h4>Preconditions</h4>
            <ul>
              {jiraFullTicketExample.preconditions.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <h4>Steps to Reproduce</h4>
            <ol>
              {jiraFullTicketExample.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <p><strong>Actual:</strong> {jiraFullTicketExample.actual}</p>
            <p><strong>Expected:</strong> {jiraFullTicketExample.expected}</p>
            <p><strong>Severity:</strong> {jiraFullTicketExample.severity}</p>
            <p><strong>Priority:</strong> {jiraFullTicketExample.priority}</p>
            <p><strong>Environment:</strong> {jiraFullTicketExample.environment}</p>
            <p><strong>Test Data:</strong> {jiraFullTicketExample.testData}</p>
            <p><strong>Attachment:</strong> {jiraFullTicketExample.attachment}</p>
            <p><strong>Root Cause:</strong> {jiraFullTicketExample.rootCause}</p>
            <p><strong>Developer Comment:</strong> {jiraFullTicketExample.developerComment}</p>
            <p><strong>QA Retest Comment:</strong> {jiraFullTicketExample.qaRetestComment}</p>
            <p><strong>Status:</strong> {jiraFullTicketExample.status}</p>
          </article>
        </section>

        {renderPrintTable("JIRA glossary", ["Term", "Meaning"], jiraGlossaryTerms)}

        <section className="jira-print-section">
          <h2>Bonus checklist</h2>
          <ul className="jira-print-bonus">
            {jiraBonusSections.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>
      </article>
    </main>
  );
}
